import { useState, type DragEvent } from 'react';

/**
 * 목록 항목을 끌어 순서를 바꾼다. 본사·현장 업무 편집기가 함께 쓴다.
 *
 * 끄는 중인 항목과 놓일 자리를 표시하기 위한 상태가 전부라, 실제 순서 변경은 `onReorder` 에 맡긴다 —
 * 본사는 목록 전체를, 현장은 같은 분류 안에서만 재정렬하기 때문이다.
 */
export function useListDrag(onReorder: (draggedId: string, targetId: string) => void) {
  const [dragId, setDragId] = useState<string | null>(null);
  const [dropTargetId, setDropTargetId] = useState<string | null>(null);

  const clear = () => {
    setDragId(null);
    setDropTargetId(null);
  };

  const dragHandlers = (id: string, enabled = true) => ({
    draggable: enabled,
    onDragStart: () => setDragId(id),
    onDragOver: (event: DragEvent) => {
      event.preventDefault();
      if (dragId && dragId !== id) setDropTargetId(id);
    },
    onDrop: () => {
      if (dragId && dragId !== id) onReorder(dragId, id);
      clear();
    },
    onDragEnd: clear,
  });

  /** 끄는 중인 항목과 놓일 자리를 알아볼 수 있게 하는 스타일. */
  const dragStyle = (id: string) => ({
    background: dragId === id ? 'var(--accent-bg)' : undefined,
    borderTop: dropTargetId === id ? '2px solid var(--danger)' : '2px solid transparent',
  });

  return { dragHandlers, dragStyle };
}

/** 배열에서 `draggedId` 를 빼내 `targetId` 자리에 끼워 넣은 새 배열. */
export function moveItem<T extends { id: string }>(items: T[], draggedId: string, targetId: string): T[] | null {
  const from = items.findIndex((item) => item.id === draggedId);
  const to = items.findIndex((item) => item.id === targetId);
  if (from < 0 || to < 0 || from === to) return null;

  const next = [...items];
  const [moved] = next.splice(from, 1);
  if (!moved) return null;
  next.splice(to, 0, moved);
  return next;
}
